import type { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { UnreachableCaseError } from 'ts-essentials';
import { getLogger } from '../logger';
import { AbstractLogger } from './abstract-logger';
import {
  ILogger,
  LogLevel,
  LoggerType,
  NewRelicLoggerConfig,
} from './logger.types';

export class NewRelicLogger extends AbstractLogger {
  private static agent?: BrowserAgent;
  private static isInitialized = false;
  private static ready: Promise<void> = Promise.resolve();
  private readonly enabled: boolean;

  public override get type(): LoggerType {
    return 'newrelic';
  }

  constructor(
    name = 'newrelic',
    logLevel: LogLevel,
    private readonly config: NewRelicLoggerConfig
  ) {
    super(name, logLevel);

    this.enabled = logLevel !== LogLevel.None;
    if (!this.enabled) return;

    if (!NewRelicLogger.isInitialized) {
      NewRelicLogger.isInitialized = true;
      // The agent assigns `NREUM` and wraps `fetch` as soon as its module
      // evaluates. Importing it from the logging barrel crashes Node tests
      // (`globalScope` is `false` outside a window) and perturbs apps that
      // only use `getLogger`. Load it when a New Relic logger is created.
      NewRelicLogger.ready = this.initialize(config).catch(
        (error: unknown): void => {
          // The agent cannot record its own startup failure, and a failed
          // attempt must not block a later logger from trying again.
          NewRelicLogger.isInitialized = false;
          getLogger().error(
            'Failed to initialize the New Relic browser agent',
            error
          );
        }
      );
    }
  }

  public createNamedLogger(name: string): ILogger {
    return new NewRelicLogger(name, this.level, this.config);
  }

  private async initialize(config: NewRelicLoggerConfig): Promise<void> {
    const { BrowserAgent } = await import(
      '@newrelic/browser-agent/loaders/browser-agent'
    );

    const agent = new BrowserAgent({
      info: {
        licenseKey: config.licenseKey,
        applicationID: config.applicationId,
        beacon: config.beacon,
        errorBeacon: config.errorBeacon,
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention -- `loader_config` is the New Relic Browser Agent's required option key.
      loader_config: {
        accountID: config.accountId,
        trustKey: config.trustKey,
        agentID: config.applicationId,
        licenseKey: config.licenseKey,
        applicationID: config.applicationId,
      },
      init: {
        logging: { enabled: true },
      },
    });

    agent.setCustomAttribute('env', config.env);
    agent.setCustomAttribute('service', config.service);
    agent.setCustomAttribute('version', config.version);
    NewRelicLogger.agent = agent;
  }

  public setProjectContext(project?: {
    id: string;
    name: string;
    location: string;
  }): void {
    if (!this.enabled) return;

    void NewRelicLogger.ready.then(() => {
      if (!NewRelicLogger.agent) return;

      // New Relic custom attributes only accept flat scalar values, so the project is split into separate keys.
      NewRelicLogger.agent.setCustomAttribute(
        'project.id',
        project?.id ?? null
      );
      NewRelicLogger.agent.setCustomAttribute(
        'project.name',
        project?.name ?? null
      );
      NewRelicLogger.agent.setCustomAttribute(
        'project.location',
        project?.location ?? null
      );
    });
  }

  protected log(
    level: LogLevel,
    message: string,
    context?: object,
    error?: Error
  ): void {
    if (!this.shouldLog(level) || !this.enabled) return;

    const loggerMessage = `[${this.name}]: ${message}`;
    const customAttributes = {
      ...context,
      ...(error
        ? { errorMessage: error.message, errorStack: error.stack }
        : {}),
    };

    void NewRelicLogger.ready.then(() => {
      if (!NewRelicLogger.agent) return;

      switch (level) {
        case LogLevel.Debug:
          NewRelicLogger.agent.log(loggerMessage, {
            level: 'DEBUG',
            customAttributes,
          });
          return;

        case LogLevel.Info:
          NewRelicLogger.agent.log(loggerMessage, {
            level: 'INFO',
            customAttributes,
          });
          return;

        case LogLevel.Warn:
          NewRelicLogger.agent.log(loggerMessage, {
            level: 'WARN',
            customAttributes,
          });
          return;

        case LogLevel.Error:
          NewRelicLogger.agent.log(loggerMessage, {
            level: 'ERROR',
            customAttributes,
          });
          return;

        default:
          throw new UnreachableCaseError(level);
      }
    });
  }
}
